import { Avatar } from '@startuptools/ui/Avatar';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Input } from '@startuptools/ui/Input';
import { ListSubheader } from '@startuptools/ui/ListSubheader';
import { Modal } from '@startuptools/ui/Modal';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Option } from '@startuptools/ui/Option';
import SearchIcon from '@mui/icons-material/Search';
import { Select } from '@startuptools/ui/Select';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import React from 'react';
import { Trans, useLingui } from '@lingui/react';
import { SelectIdentityProps, useFindIdentity } from '../add/FindIdentityDialog';
import { InputState, JuridicalKind } from '../types';
import { IDKind } from '@startuptools/common/common';
import { type DisplayIdentity } from '../Identity';
import { IdentityDisplay } from '../IdentityDisplay';
import Fuse from 'fuse.js';
import { Identity } from '../Identity';
import { type GqlCreateIdentityInput } from '../../../graphql/base-types.graphql';
import { Checkbox } from '@startuptools/ui/joy/Checkbox';

type Props = {
  value: string | string[] | null | undefined;
  identities?: (DisplayIdentity & { value: string })[];
  identity?: DisplayIdentity | GqlCreateIdentityInput | null | undefined;
  handleCreate: SelectIdentityProps['handleCreate'];
  emptyState?: React.ReactNode;
  allowCreation?: boolean;
  /* this defines whether there are multiple identities to choose from */
  multiple?: boolean;
  /* this defines whether multiple identities can be selected */
  multipleSelectable?: boolean;
  paywall?: React.ReactNode | undefined;
  juridicalKinds?: SelectIdentityProps['juridicalKinds'];
  createButton?: React.ComponentType<{ onClick: () => void }>;
  includeFictitious?: boolean;
  availableInputStates?: SelectIdentityProps['availableInputStates'];
  detailFieldsSettings?: SelectIdentityProps['detailFieldsSettings'];
  companyBoardOrgNumber?: string;
  onDeselect?: (value: string | string[]) => void;
} & Pick<React.ComponentProps<typeof Select>, 'onChange' | 'name' | 'placeholder' | 'sx'>;

const CreateButton = (props: { onClick: () => void }) => {
  return (
    <Button sx={{ flexShrink: 0 }} {...props}>
      <Trans id="Add new" />
    </Button>
  );
};

export const SelectIdentity = ({
  identities,
  identity,
  value,
  handleCreate,
  emptyState = <Trans id="None found" />,
  createButton: Create = CreateButton,
  allowCreation = true,
  multiple = true,
  multipleSelectable = false,
  includeFictitious = false,
  sx,
  paywall,
  juridicalKinds = [JuridicalKind.Company, JuridicalKind.Person],
  availableInputStates = {
    [InputState.System]: true,
    [InputState.Swedish]: true,
    [InputState.Foreign]: true,
    [InputState.CompanyBoard]: false,
    [InputState.Fictitious]: false,
  },
  detailFieldsSettings,
  companyBoardOrgNumber,
  onChange,
  onDeselect,
  name,
  placeholder,
}: Props) => {
  const { i18n } = useLingui();
  const { setState } = useFindIdentity();
  const [showPaywall, setShowPaywall] = React.useState(false);

  const setShowDialog = React.useCallback(
    (show: boolean) => {
      if (paywall) {
        setShowPaywall(show);
      } else {
        setState(
          show
            ? {
                defaultInputState: InputState.System,
                headline: value ? <Trans id="Update" /> : <Trans id="Select" />,
                juridicalKinds,
                availableInputStates,
                companyBoardOrgNumber,
                detailFieldsSettings,
                onClose: () => setShowDialog(false),
                handleCreate: async (...args) => {
                  await handleCreate(...args);
                  setShowDialog(false);
                },
              }
            : null,
        );
      }
    },
    [
      setState,
      handleCreate,
      value,
      juridicalKinds,
      availableInputStates,
      paywall,
      companyBoardOrgNumber,
      detailFieldsSettings,
    ],
  );
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>();

  const filteredIdentities = React.useMemo(() => {
    if (!identities) return [];
    const relevantIdentities = identities.filter(i => {
      if (!includeFictitious) {
        return i.identityNumberKind !== IDKind.Fictitious;
      }
      return true;
    });
    if (!searchQuery) return relevantIdentities;
    const fuse = new Fuse(relevantIdentities, {
      includeScore: true,
      shouldSort: true,
      threshold: 0.6,
      distance: 80,
      keys: [
        { name: 'name', weight: 5 },
        { name: 'identityNumber', weight: 5 },
        { name: 'email', weight: 1 },
      ],
    });
    return fuse.search(searchQuery).map(e => e.item);
  }, [identities, includeFictitious, searchQuery]);

  const createButton = allowCreation ? <Create onClick={() => setShowDialog(true)} /> : null;

  return (
    <Stack direction="row" gap={2} sx={sx}>
      {multiple ? (
        <>
          <Select
            renderValue={
              multiple
                ? selected => {
                    if (Array.isArray(selected)) {
                      return <Typography level="body-sm">{selected.map(i => i.label).join(', ')}</Typography>;
                    }
                    return <Typography level="body-sm">{selected?.label}</Typography>;
                  }
                : undefined
            }
            multiple={multipleSelectable}
            onChange={(event, value) => {
              if (Array.isArray(value) && value.includes('select-all')) {
                if (identities?.length) {
                  const allValues = filteredIdentities.map(i => i.value);
                  const isDeselecting = value.length >= allValues.length;
                  if (isDeselecting) {
                    onDeselect?.(allValues);
                  } else {
                    onChange?.(event, allValues);
                  }
                  return;
                }
              }
              // Filter out the "select-all" value from the selection
              const filteredValue = Array.isArray(value) ? value.filter(v => v !== 'select-all') : value;

              if (typeof filteredValue === 'string' && filteredValue === 'empty-val') {
                return;
              }
              onChange?.(event, filteredValue);
            }}
            name={name}
            value={value}
            placeholder={placeholder}
            sx={{
              minWidth: '250px',
              flexGrow: 1,
            }}
            autoFocus={false}
            onClose={() => {
              setSearchQuery(undefined);
            }}
          >
            {Boolean(filteredIdentities.length) && (
              <ListSubheader sx={{ mb: 1 }}>
                <Input
                  autoFocus
                  placeholder={i18n._('Type to search')}
                  fullWidth
                  startDecorator={<SearchIcon />}
                  onChange={e => setSearchQuery(e.target.value)}
                  value={searchQuery ?? ''}
                  onKeyDown={e => {
                    if (e.key !== 'Escape') {
                      // Prevents autoselecting item while typing
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
            )}

            {multipleSelectable && filteredIdentities.length > 0 && (
              <Option value="select-all">
                <Checkbox
                  checked={value?.length === filteredIdentities.length}
                  indeterminate={Boolean(value?.length && value.length < filteredIdentities.length)}
                />
                <Typography level="body-sm">
                  {value?.length === filteredIdentities.length ? (
                    <Trans id="Deselect All" />
                  ) : (
                    <Trans id="Select All" />
                  )}
                </Typography>
              </Option>
            )}

            {filteredIdentities.map(i => (
              <Option key={i.id} value={i.value} label={i.name}>
                {multipleSelectable && <Checkbox checked={value?.includes(i.value)} />}
                <Identity identity={i} editable={false} />
              </Option>
            ))}
            {!identities?.length && (
              /* this Option has be to be here, otherwise the Select does not close onBlur, weirdly enough. */
              <Option value="empty-val">
                <Box px={3} py={1}>
                  <Typography level="body-sm">{emptyState}</Typography>
                </Box>
              </Option>
            )}
          </Select>
          {createButton}
        </>
      ) : (
        <Stack direction="row" gap={1}>
          {identity ? (
            <Identity identity={identity} />
          ) : (
            <IdentityDisplay
              startDecorator={
                <Box>
                  <Avatar size="md" />
                </Box>
              }
              name={<Typography sx={{ fontStyle: 'italic' }}>{placeholder || <Trans id="None selected" />}</Typography>}
            />
          )}
          <Box>{createButton}</Box>
        </Stack>
      )}
      {showPaywall && (
        <Modal open onClose={() => setShowPaywall(false)}>
          <ModalDialog>
            <Box mt={2}>{paywall}</Box>

            <ModalClose />
          </ModalDialog>
        </Modal>
      )}
    </Stack>
  );
};
